import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Save, DollarSign, ChevronDown, ChevronRight, RotateCcw, Info, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import useHiveTypes from '../hooks/useHiveTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section colors for dynamic categories
const SECTION_COLORS = {
  protective_gear: { icon: '🧤', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
  hive_tools: { icon: '🔧', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
  feeding_equipment: { icon: '🍯', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
  harvesting_equipment: { icon: '🧴', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
  heavy_equipment: { icon: '📦', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-800' },
  miscellaneous: { icon: '📋', bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800' },
  lids_covers: { icon: '🏠', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-800' },
  feeders: { icon: '🍯', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
  queen_bee_management: { icon: '👑', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
  bottom_boards_stands: { icon: '⬇️', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
  pest_control: { icon: '🛡️', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
  heavy_accessories: { icon: '⚙️', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800' },
};
const DEFAULT_COLOR = { icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' };

const SECTION_LABELS = {
  protective_gear: 'Protective Gear', hive_tools: 'Hive Tools', feeding_equipment: 'Feeding Equipment',
  harvesting_equipment: 'Harvesting Equipment', heavy_equipment: 'Heavy Equipment', miscellaneous: 'Miscellaneous',
  lids_covers: 'Lids & Covers', feeders: 'Feeders', queen_bee_management: 'Queen Management',
  bottom_boards_stands: 'Bottom Boards & Stands', pest_control: 'Pest Control', heavy_accessories: 'Heavy & Accessories',
};

// Structural categories always present (hive types, frames, custom costs)
const STRUCTURAL_CATEGORIES = {
  hive_types: {
    label: 'Hive Types', icon: '📦', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800',
    items: [
      { key: 'langstroth_deep_brood_price', label: 'Langstroth Deep Brood Box' },
      { key: 'langstroth_deep_super_price', label: 'Langstroth Deep Super' },
      { key: 'langstroth_ideal_super_price', label: 'Langstroth Ideal Super' },
      { key: 'flow_hive_brood_price', label: 'Flow Hive Brood Box' },
      { key: 'flow_hive_super_price', label: 'Flow Hive Super' },
      { key: 'national_deep_price', label: 'National Deep Box' },
      { key: 'national_shallow_price', label: 'National Shallow Box' },
      { key: 'warre_box_price', label: 'Warre Box' },
      { key: 'top_bar_hive_price', label: 'Top Bar Hive Body' },
      { key: 'nuc_box_price', label: 'NUC Box' },
    ]
  },
  frames: {
    label: 'Frames & Foundation', icon: '🖼️', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800',
    items: [
      { key: 'deep_frames_price', label: 'Deep Frames (each)' },
      { key: 'medium_frames_price', label: 'Medium Frames (each)' },
      { key: 'shallow_frames_price', label: 'Shallow Frames (each)' },
      { key: 'wax_foundation_price', label: 'Wax Foundation (each)' },
      { key: 'plastic_foundation_price', label: 'Plastic Foundation (each)' },
      { key: 'flow_frames_price', label: 'Flow Frames (set)' },
    ]
  },
  custom: {
    label: 'Custom & Other', icon: '📝', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800',
    items: [
      { key: 'shipping_price', label: 'Shipping/Delivery' },
      { key: 'labor_price', label: 'Assembly/Labor' },
      { key: 'other_costs', label: 'Other Costs' },
    ]
  },
};

// Build dynamic categories from admin DB items
function buildDynamicCategories(adminItems, customCategories = []) {
  const categories = {};
  const catLabels = { ...SECTION_LABELS };
  customCategories.forEach(c => { catLabels[c.key] = c.name; });
  adminItems.filter(item => item.is_active !== false).forEach(item => {
    const section = item.section || 'miscellaneous';
    if (!categories[section]) {
      const colors = SECTION_COLORS[section] || DEFAULT_COLOR;
      categories[section] = {
        label: catLabels[section] || section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        ...colors, items: [],
      };
    }
    const formKey = item.form_key || item.name.toLowerCase().trim().replace(/[\s\/()]+/g, '_');
    const priceKey = `${formKey}_price`;
    if (!categories[section].items.some(i => i.key === priceKey)) {
      categories[section].items.push({ key: priceKey, label: item.name });
    }
  });
  return categories;
}
const HiveCosts = () => {
  const navigate = useNavigate();
  const { userTypes } = useHiveTypes();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prices, setPrices] = useState({});
  const [customItems, setCustomItems] = useState({}); // { categoryKey: [{key, label}] }
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('components');
  const [hasChanges, setHasChanges] = useState(false);
  const [addingTo, setAddingTo] = useState(null); // category key being added to
  const [newItemName, setNewItemName] = useState('');
  const [adminComponents, setAdminComponents] = useState([]);
  const [adminTools, setAdminTools] = useState([]);
  const [customToolCategories, setCustomToolCategories] = useState([]);
  const [customCompCategories, setCustomCompCategories] = useState([]);

  // Fetch admin tools and components
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const headers = { Authorization: `Bearer ${token}` };
      Promise.all([
        axios.get(`${API}/config/admin_components`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/admin_tools`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/custom-categories/tools`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/custom-categories/components`, { headers }).catch(() => ({ data: [] })),
      ]).then(([compRes, toolRes, toolCatRes, compCatRes]) => {
        setAdminComponents(compRes.data || []);
        setAdminTools(toolRes.data || []);
        setCustomToolCategories(toolCatRes.data || []);
        setCustomCompCategories(compCatRes.data || []);
      });
    }
  }, []);

  // Build dynamic component categories from admin DB
  const getComponentCategories = useCallback(() => {
    // Start with structural categories (hive types, frames, custom)
    const categories = JSON.parse(JSON.stringify(STRUCTURAL_CATEGORIES));

    // Build component categories dynamically from admin_components
    const compCategories = buildDynamicCategories(adminComponents, customCompCategories);
    Object.entries(compCategories).forEach(([key, cat]) => {
      categories[key] = cat;
    });

    // Merge user hive types
    if (userTypes.length > 0) {
      const allBoxes = new Set();
      const allFrames = new Set();
      const allFoundations = new Set();
      userTypes.forEach(type => {
        (type.box_types || []).forEach(b => allBoxes.add(b));
        (type.frame_types || []).forEach(f => allFrames.add(f));
        (type.foundation_types || []).forEach(f => allFoundations.add(f));
      });
      allBoxes.forEach(box => {
        const key = `custom_box_${box.toLowerCase().replace(/[\s\/()]+/g, '_')}_price`;
        if (!categories.hive_types.items.some(i => i.key === key)) {
          categories.hive_types.items.push({ key, label: `${box} Box` });
        }
      });
      allFrames.forEach(frame => {
        const key = `custom_frame_${frame.toLowerCase().replace(/[\s\/()]+/g, '_')}_price`;
        if (!categories.frames.items.some(i => i.key === key)) {
          categories.frames.items.push({ key, label: `${frame} Frames` });
        }
      });
      allFoundations.forEach(foundation => {
        const key = `custom_foundation_${foundation.toLowerCase().replace(/[\s\/()]+/g, '_')}_price`;
        if (!categories.frames.items.some(i => i.key === key)) {
          categories.frames.items.push({ key, label: `${foundation} Foundation` });
        }
      });
    }

    // Merge saved custom items
    Object.entries(customItems).forEach(([catKey, items]) => {
      if (categories[catKey]) {
        items.forEach(item => {
          if (!categories[catKey].items.some(i => i.key === item.key)) {
            categories[catKey].items.push(item);
          }
        });
      }
    });

    return categories;
  }, [userTypes, customItems, adminComponents, customCompCategories]);

  // Build dynamic tool categories from admin DB
  const getToolCategories = useCallback(() => {
    const categories = buildDynamicCategories(adminTools, customToolCategories);

    // Merge saved custom items
    Object.entries(customItems).forEach(([catKey, items]) => {
      if (categories[catKey]) {
        items.forEach(item => {
          if (!categories[catKey].items.some(i => i.key === item.key)) {
            categories[catKey].items.push(item);
          }
        });
      }
    });

    return categories;
  }, [adminTools, customItems, customToolCategories]);

  // Fetch saved prices
  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token && localStorage.getItem('demoMode') === 'true') {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/hive-costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrices(response.data.prices || {});
      setCustomItems(response.data.custom_items || {});
    } catch (error) {
      setPrices({});
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = useCallback((key, value) => {
    setPrices(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
    setHasChanges(true);
  }, []);

  const savePrices = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/hive-costs`, { prices, custom_items: customItems }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default prices saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save prices');
    } finally {
      setSaving(false);
    }
  };

  const resetPrices = () => {
    setPrices({});
    setHasChanges(true);
    toast.info('Prices reset - click Save to confirm');
  };

  const addCustomItem = (categoryKey) => {
    if (!newItemName.trim()) return;
    const key = `custom_${newItemName.trim().toLowerCase().replace(/[\s\/()]+/g, '_')}_price`;
    const item = { key, label: newItemName.trim() };
    setCustomItems(prev => ({
      ...prev,
      [categoryKey]: [...(prev[categoryKey] || []), item]
    }));
    setNewItemName('');
    setAddingTo(null);
    setHasChanges(true);
    toast.success(`"${newItemName.trim()}" added`);
  };

  const removeCustomItem = (categoryKey, itemKey) => {
    setCustomItems(prev => ({
      ...prev,
      [categoryKey]: (prev[categoryKey] || []).filter(i => i.key !== itemKey)
    }));
    setPrices(prev => { const next = { ...prev }; delete next[itemKey]; return next; });
    setHasChanges(true);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const renderPriceInput = (item, categoryKey) => {
    const isCustom = (customItems[categoryKey] || []).some(ci => ci.key === item.key);
    return (
      <div key={item.key} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-stone-700">{item.label}</span>
          {isCustom && (
            <button onClick={() => removeCustomItem(categoryKey, item.key)} className="text-stone-300 hover:text-red-500 transition-colors" title="Remove custom item">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="relative w-28">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={prices[item.key] || ''}
            onChange={(e) => handlePriceChange(item.key, e.target.value)}
            placeholder="0.00"
            className="h-9 pl-6 text-sm text-right"
          />
        </div>
      </div>
    );
  };

  const renderCategory = (categoryKey, category) => {
    const isExpanded = expandedSections[categoryKey];
    const filledCount = category.items.filter(item => prices[item.key] > 0).length;
    
    return (
      <div key={categoryKey} className={`rounded-xl border ${category.border} overflow-hidden mb-3`}>
        <button
          onClick={() => toggleSection(categoryKey)}
          className={`w-full px-4 py-3 flex items-center justify-between ${category.bg} hover:opacity-90 transition-opacity`}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg">{category.icon}</span>
            <span className={`font-semibold ${category.text}`}>{category.label}</span>
            {filledCount > 0 && (
              <span className="text-xs bg-white px-2 py-0.5 rounded-full text-stone-600">
                {filledCount}/{category.items.length}
              </span>
            )}
          </div>
          {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        {isExpanded && (
          <div className="bg-white p-4">
            {category.items.map(item => renderPriceInput(item, categoryKey))}
            {/* Add Custom Item */}
            {addingTo === categoryKey ? (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100">
                <Input
                  autoFocus
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCustomItem(categoryKey); }}
                  placeholder="Custom item name..."
                  className="h-8 text-sm flex-1"
                  data-testid={`custom-item-input-${categoryKey}`}
                />
                <Button size="sm" onClick={() => addCustomItem(categoryKey)} className="h-8 bg-amber-600 hover:bg-amber-700">Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddingTo(null); setNewItemName(''); }} className="h-8">Cancel</Button>
              </div>
            ) : (
              <button
                onClick={() => setAddingTo(categoryKey)}
                className="flex items-center gap-1 mt-3 pt-3 border-t border-stone-100 text-xs text-amber-600 hover:text-amber-800 transition-colors"
                data-testid={`add-custom-item-${categoryKey}`}
              >
                <Plus className="h-3.5 w-3.5" /> Add custom item
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="honeycomb-bg pb-24">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-amber-600" />
              Hive Costs
            </h1>
            <p className="text-sm text-stone-500 mt-1">Set default prices for hive components</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={resetPrices}
              className="text-stone-600"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
            <Button
              onClick={savePrices}
              disabled={saving || !hasChanges}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Defaults'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl p-4">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">How it works</p>
            <p className="text-sm text-blue-700 mt-1">
              Set your default prices here. When you add a new hive, these prices will auto-populate 
              in the Pricing section. You can still adjust prices for individual hives if needed.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6 bg-stone-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('components')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
              activeTab === 'components' 
                ? 'bg-white text-stone-800 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
          >
            📦 Hive Components
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
              activeTab === 'tools' 
                ? 'bg-white text-stone-800 shadow-sm' 
                : 'text-stone-600 hover:text-stone-800'
            }`}
          >
            🔧 Tools & Equipment
          </button>
        </div>

        {/* Component Categories */}
        {activeTab === 'components' && (
          <div className="space-y-1">
            {Object.entries(getComponentCategories()).map(([key, category]) => 
              renderCategory(key, category)
            )}
          </div>
        )}

        {/* Tool Categories */}
        {activeTab === 'tools' && (
          <div className="space-y-1">
            {Object.entries(getToolCategories()).map(([key, category]) => 
              renderCategory(key, category)
            )}
          </div>
        )}

        {/* Save Button at bottom */}
        {hasChanges && (
          <div className="fixed bottom-20 left-0 right-0 p-4 bg-gradient-to-t from-stone-100 to-transparent z-40 md:pl-[200px]">
            <div className="max-w-4xl mx-auto">
              <Button
                onClick={savePrices}
                disabled={saving}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-lg font-semibold rounded-xl shadow-lg"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Saving...' : 'Save Default Prices'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HiveCosts;
